/**
 * Momentum Score — Compute the composite momentum score from signals
 *
 * The score is a weighted average of four dimensions:
 *   revenueReadiness, serviceHealth, cashVelocity, growthSignal
 *
 * Weights come from the tenant's BusinessIntent config.
 * Each dimension score is derived from the severity distribution
 * of signals in that dimension.
 */

import type {
  MomentumSignal,
  MomentumScore,
  MomentumDimensions,
  MomentumSeverity,
  MomentumDimension,
  BusinessIntent,
} from './types';
import { signalDimension, sortSignals } from './types';

// ─── Default weights per business intent ─────────────────────────────────────

const INTENT_WEIGHTS: Record<BusinessIntent, MomentumDimensions> = {
  cash_velocity: {
    revenueReadiness: 0.4,
    serviceHealth: 0.2,
    cashVelocity: 0.3,
    growthSignal: 0.1,
  },
  service_sla: {
    revenueReadiness: 0.2,
    serviceHealth: 0.4,
    cashVelocity: 0.2,
    growthSignal: 0.2,
  },
  volume_growth: {
    revenueReadiness: 0.2,
    serviceHealth: 0.2,
    cashVelocity: 0.2,
    growthSignal: 0.4,
  },
  compliance_first: {
    revenueReadiness: 0.2,
    serviceHealth: 0.3,
    cashVelocity: 0.2,
    growthSignal: 0.3,
  },
};

export function getDefaultWeights(intent: BusinessIntent): MomentumDimensions {
  return INTENT_WEIGHTS[intent] ?? INTENT_WEIGHTS.cash_velocity;
}

// ─── Dimension score from signals ────────────────────────────────────────────

const SEVERITY_PENALTY: Record<MomentumSeverity, number> = {
  critical: 30,
  warning: 15,
  info: 0,
  positive: -5, // positive signals boost score
};

function dimensionScore(signals: MomentumSignal[], dimension: MomentumDimension): number {
  const dimSignals = signals.filter((s) => signalDimension(s.type) === dimension);
  if (dimSignals.length === 0) return 85; // no signals = assume healthy

  let penalty = 0;
  for (const s of dimSignals) {
    penalty += SEVERITY_PENALTY[s.severity];
  }
  return Math.max(0, Math.min(100, 100 - penalty));
}

// ─── Compute MomentumScore ───────────────────────────────────────────────────

export interface ComputeScoreOpts {
  signals: MomentumSignal[];
  weights?: MomentumDimensions;
  intent?: BusinessIntent;
  previousScore?: number;
}

export function computeMomentumScore(opts: ComputeScoreOpts): MomentumScore {
  const { signals, intent = 'cash_velocity', previousScore } = opts;
  const weights = opts.weights ?? getDefaultWeights(intent);

  const dimensions: MomentumDimensions = {
    revenueReadiness: dimensionScore(signals, 'revenue'),
    serviceHealth: dimensionScore(signals, 'service'),
    cashVelocity: dimensionScore(signals, 'cash'),
    growthSignal: dimensionScore(signals, 'growth'),
  };

  const overall = Math.round(
    dimensions.revenueReadiness * weights.revenueReadiness +
    dimensions.serviceHealth * weights.serviceHealth +
    dimensions.cashVelocity * weights.cashVelocity +
    dimensions.growthSignal * weights.growthSignal,
  );

  const trendDelta = previousScore != null ? overall - previousScore : 0;
  const trend = trendDelta > 0 ? 'up' : trendDelta < 0 ? 'down' : 'stable';

  const signalCounts: Record<MomentumSeverity, number> = {
    critical: 0,
    warning: 0,
    info: 0,
    positive: 0,
  };
  for (const s of signals) {
    signalCounts[s.severity]++;
  }

  const topSignals = sortSignals(signals).slice(0, 5);

  return {
    overall,
    dimensions,
    trend,
    trendDelta,
    signalCounts,
    topSignals,
    computedAt: new Date().toISOString(),
  };
}
